import axios from 'axios';
import { Room } from '../contexts/RoomContext';
import { ReservationFormData } from '../contexts/ReservationContext';

// Mock data for fallback when API is not available
const mockRoomsData: Room[] = [
  {
    id: 1,
    name: 'La Inquisición',
    slug: 'la-inquisicion',
    short_description: 'Una experiencia medieval llena de misterio',
    full_description: 'Sumérgete en los oscuros secretos de la Inquisición española. Resuelve enigmas históricos y escapa antes de que sea demasiado tarde.',
    hero_image: '/images/inquisicion-hero.webp',
    thumbnail_image: '/images/inquisicion-thumb.webp',
    video_url: '/videos/inquisicion_audio.mp4',
    base_price: 25,
    is_active: true,
  },
  {
    id: 2,
    name: 'El Purgatorio',
    slug: 'el-purgatorio',
    short_description: 'Un viaje al más allá',
    full_description: 'Atraviesa las puertas del purgatorio y encuentra tu camino hacia la redención en esta experiencia sobrenatural.',
    hero_image: '/images/purgatorio-hero.webp',
    thumbnail_image: '/images/purgatorio-hero.webp',
    video_url: '/videos/purgatorio_audio.mp4',
    base_price: 30,
    is_active: true,
  },
];

// Mock API implementation
const mockRoomsApi = {
  getRooms: async (): Promise<Room[]> => {
    return Promise.resolve(mockRoomsData);
  },
  getRoom: async (id: number): Promise<Room> => {
    const room = mockRoomsData.find(r => r.id === id);
    if (!room) {
      throw new Error(`Room with id ${id} not found`);
    }
    return Promise.resolve(room);
  },
};

// Helper function to generate local URLs when API returns null
const getLocalUrl = (slug: string, type: 'hero' | 'thumb' | 'video'): string => {
  const urlMap: Record<string, Record<string, string>> = {
    'la-inquisicion': {
      hero: '/images/inquisicion-hero.webp',
      thumb: '/images/inquisicion-thumb.webp',
      video: '/videos/inquisicion_audio.mp4',
    },
    'el-purgatorio': {
      hero: '/images/purgatorio-hero.webp',
      thumb: '/images/purgatorio-hero.webp', // Using hero image as thumb since thumb doesn't exist
      video: '/videos/purgatorio_audio.mp4',
    },

  };

  return urlMap[slug]?.[type] || '';
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth tokens
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth tokens
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('adminUsername');
      
      // Redirect to admin login if we're in an admin route
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin';
      }
    }
    return Promise.reject(error);
  }
);

// Room API endpoints
export const roomsApi = {
  // Get all active rooms
  getRooms: async (): Promise<Room[]> => {
    // Always try API first if URL is configured
    if (process.env.REACT_APP_API_URL) {
      try {
        const response = await api.get('/rooms/');
        // Fix null URLs by using local fallbacks
        const rooms = response.data.map((room: Room) => ({
          ...room,
          hero_image: room.hero_image || getLocalUrl(room.slug, 'hero'),
          thumbnail_image: room.thumbnail_image || getLocalUrl(room.slug, 'thumb'),
          video_url: room.video_url || getLocalUrl(room.slug, 'video'),
        }));
        return rooms;
      } catch (error) {
        console.warn('API not available, falling back to mock data');
        return mockRoomsApi.getRooms();
      }
    }
    
    // If no API URL is configured, use mock data
    return mockRoomsApi.getRooms();
  },

  // Get room by ID
  getRoom: async (id: number): Promise<Room> => {
    // Always try API first if URL is configured
    if (process.env.REACT_APP_API_URL) {
      try {
        const response = await api.get(`/rooms/${id}/`);
        const room = response.data;
        // Fix null URLs by using local fallbacks
        return {
          ...room,
          hero_image: room.hero_image || getLocalUrl(room.slug, 'hero'),
          thumbnail_image: room.thumbnail_image || getLocalUrl(room.slug, 'thumb'),
          video_url: room.video_url || getLocalUrl(room.slug, 'video'),
        };
      } catch (error) {
        console.warn('API not available, falling back to mock data');
        return mockRoomsApi.getRoom(id);
      }
    }
    
    // If no API URL is configured, use mock data
    return mockRoomsApi.getRoom(id);
  },

  // Get available time slots for a room on a specific date
  getAvailability: async (roomId: number, date: string): Promise<string[]> => {
    try {
      console.log(`Fetching availability for room ${roomId} on ${date}...`);
      const response = await api.get(`/rooms/${roomId}/availability/`, {
        params: { date }
      });
      console.log('Availability API response:', response.data);
      
      // Extract time strings from the API response
      const availableTimes = response.data.available_times.map((slot: any) =>
        slot.time.substring(0, 5) // Convert "HH:MM:SS" to "HH:MM"
      );
      
      console.log('Available times:', availableTimes);
      return availableTimes;
    } catch (error) {
      console.error(`Error fetching availability for room ${roomId} on ${date}:`, error);
      throw new Error('No se pudieron cargar los horarios disponibles. Verifica tu conexión e intenta nuevamente.');
    }
  },

  // Get all time slots (available and reserved) for a room on a specific date
  getAllSlots: async (roomId: number, date: string): Promise<{time: string, status: string}[]> => {
    try {
      console.log(`Fetching all slots for room ${roomId} on ${date}...`);
      const response = await api.get(`/rooms/${roomId}/all-slots/`, {
        params: { date }
      });
      console.log('All slots API response:', response.data);
      
      // Extract time and status from the API response
      const allSlots = response.data.available_times.map((slot: any) => ({
        time: slot.time.substring(0, 5), // Convert "HH:MM:SS" to "HH:MM"
        status: slot.status
      }));
      
      console.log('All slots:', allSlots);
      return allSlots;
    } catch (error) {
      console.error(`Error fetching all slots for room ${roomId} on ${date}:`, error);
      // Fallback to availability endpoint if all-slots fails
      const availableTimes = await roomsApi.getAvailability(roomId, date);
      return availableTimes.map(time => ({ time, status: 'active' }));
    }
  },
};

// Reservation API endpoints
export const reservationsApi = {
  // Create a new reservation
  createReservation: async (data: Omit<ReservationFormData, 'totalPrice'>): Promise<any> => {
    const response = await api.post('/reservations/', {
      room_id: data.roomId,
      date: data.date,
      time: data.time,
      num_people: data.numPeople,
      customer_name: data.customerName,
      customer_email: data.customerEmail,
      customer_phone: data.customerPhone,
    });
    return response.data;
  },
};

// Admin API endpoints
export const adminApi = {
  // Login
  login: async (username: string, password: string): Promise<{ access_token: string; refresh_token: string }> => {
    const response = await api.post('/auth/login', {
      username,
      password,
    });
    return response.data;
  },

  // Get all reservations (admin only)
  getReservations: async (timeFilter?: 'active' | 'past'): Promise<any[]> => {
    const params = timeFilter ? { time_filter: timeFilter } : {};
    const response = await api.get('/admin/reservations/', { params });
    return response.data.reservations; // Extract reservations from paginated response
  },

  // Update reservation status (admin only)
  updateReservationStatus: async (id: number, status: string): Promise<any> => {
    const response = await api.patch(`/admin/reservations/${id}/`, {
      status,
    });
    return response.data;
  },

  // Update reservation date/time (admin only)
  updateReservationDateTime: async (id: number, date: string, time: string): Promise<any> => {
    const response = await api.patch(`/admin/reservations/${id}/reschedule/`, {
      date,
      time,
    });
    return response.data;
  },

  // Update number of people (admin only)
  updateReservationPeople: async (id: number, numPeople: number): Promise<any> => {
    const response = await api.patch(`/admin/reservations/${id}/people/`, {
      num_people: numPeople,
    });
    return response.data;
  },

  // Get basic stats (admin only)
  getStats: async (): Promise<any> => {
    const response = await api.get('/admin/stats/');
    return response.data;
  },
};

export default api;
